import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  makeStyles,
  Typography,
  IconButton,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../util/ApiHelper";
import { toast } from "react-toastify";
import editorConfiguration from "../../../util/EditorConfiguration";
import { useLoading } from "../../../util/LoadingProvider";
import ImageUploadField from "../../common/ImageUploadField";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import dayjs from "dayjs";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Link } from "react-router-dom";

const schema = yup.object().shape({
  localizations: yup.object().shape({
    en: yup.object().shape({
      title: yup.string(),
      subtitle: yup.string().nullable(),
      content: yup.string(),
      image: yup.string().nullable(),
    }),
    tc: yup.object().shape({
      title: yup.string(),
      subtitle: yup.string().nullable(),
      content: yup.string(),
      image: yup.string().nullable(),
    }),
  }),
  image: yup.string().nullable(),
  publishTime: yup.string().nullable(),
  status: yup.string(),
  type: yup.string(),
});

const defaultFormValues = {
  localizations: {
    en: {
      title: "",
      subtitle: "",
      content: "",
      image: "",
    },
    tc: {
      title: "",
      subtitle: "",
      content: "",
      image: "",
    },
  },
  image: "",
  publishTime: dayjs().format("YYYY-MM-DDTHH:mm:00"),
  status: "ACTIVE",
  type: "public",
};

// Create a custom editor configuration for news with membership upload
const newsEditorConfig = {
  ...editorConfiguration,
  site: 'membership',
  folder: 'NEWS',
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",
    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    padding: theme.spacing(3),
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "40px",
    textAlign: "left",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionText: {
    fontSize: "13px",
    fontWeight: 700,
    paddingLeft: "4px",
    marginBottom: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));

export default function NewsEditingPage({ disabled, ...props }) {
  const { match, history } = props;
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const { setLoading } = useLoading();
  const classes = useStyles();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    console.log("Form submitted with values:", value);
    console.log("Current id:", id);
    // Check if we're in create mode (id is undefined or "create")
    if (!id || id === "create") {
      createNews(value);
    } else {
      updateNews(value);
    }
  };

  const onError = (errors) => {
    console.log("Form validation errors:", errors);
    toast.error("Please fix the form errors before submitting.");
  };

  const getNews = useCallback(async () => {
    // Don't fetch if id is "create" or undefined
    if (!id || id === "create") {
      return;
    }
    
    setLoading(true);
    try {
      const result = await apiHelper.cineclub.getMembershipNewsById(id);
      console.log(result);
      setDefaultValues(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load news.");
    }
    setLoading(false);
  }, [id, setLoading, reset]);

  const updateNews = async (value) => {
    try {
      setLoading(true);
      // check publish time - use current time if not set or invalid
      if (!value.publishTime || !dayjs(value.publishTime).isValid()) {
        value.publishTime = dayjs().format("YYYY-MM-DDTHH:mm:00");
      } else {
        value.publishTime = dayjs(value.publishTime).format("YYYY-MM-DDTHH:mm:00");
      }

      console.log("Updating news with data:", value);
      const result = await apiHelper.cineclub.updateMembershipNews(id, value);
      console.log("Update result:", result);
      
      if (result.status === 200 || result.status === 201) {
        toast.dark("Your changes are saved.");
        // Refresh the news data
        await getNews();
      } else {
        toast.error("Failed to update news. Please try again.");
      }
    } catch (err) {
      console.error("Error updating news:", err);
      toast.error("Failed to update news. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const createNews = async (value) => {
    try {
      setLoading(true);
      // check publish time - use current time if not set or invalid
      if (!value.publishTime || !dayjs(value.publishTime).isValid()) {
        value.publishTime = dayjs().format("YYYY-MM-DDTHH:mm:00");
      } else {
        value.publishTime = dayjs(value.publishTime).format("YYYY-MM-DDTHH:mm:00");
      }

      console.log("Creating news with data:", value);
      const result = await apiHelper.cineclub.createMembershipNews(value);
      console.log("Create result:", result);
      
      if (result.status === 200 || result.status === 201) {
        // Extract the ID from the response - check different possible response structures
        const newsId = result.data?.id || 
                      result.data?.data?.id || 
                      (result.data?.data && typeof result.data.data === 'object' && result.data.data.id) ||
                      (result.data && result.data.code === 0 && result.data.data?.id);
        
        if (newsId) {
          toast.dark("The news is created.");
          history.push(`/cineclub/membership/news/${newsId}/edit`);
        } else {
          console.log("Could not extract news ID from response, redirecting to list:", result.data);
          toast.dark("The news is created.");
          // Redirect to listing page
          history.push(`/cineclub/membership/news`);
        }
      } else {
        toast.error("Failed to create news. Please try again.");
      }
    } catch (err) {
      console.error("Error creating news:", err);
      toast.error("Failed to create news. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (field, file) => {
    try {
      setLoading(true);
      const result = await apiHelper.cineclub.uploadMembershipFile(file, "NEWS");
      const imageUrl = result.data.url || result.data.data || result.data;
      
      setValue(field, imageUrl);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    } finally {
      setLoading(false);
    }
  };

  const handleImageDelete = (field) => {
    setValue(field, "");
  };

  useEffect(() => {
    // Only fetch news if we have a valid id (not create mode)
    if (id && id !== "create") {
      getNews();
    }
  }, [getNews, id]);

  const watchedValues = watch();

  return (
    <div className={classes.content}>
      <Box className={classes.header}>
        <IconButton
          component={Link}
          to="/cineclub/membership/news"
          className={classes.backButton}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          News &gt; {(!id || id === "create") ? "Add New News" : "Edit News"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <fieldset disabled={disabled} className="fieldset">
          <Grid container xs={12} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Title (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Title (Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.subtitle"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Subtitle (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.subtitle"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Subtitle (Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>

          {defaultValues.localizations && (
            <>
              <Box
                fontWeight={900}
                fontSize={13}
                mb={2}
                style={{ marginTop: "30px" }}
                width="100%"
              >
                <Typography variant="p">Content (English)</Typography>
              </Box>

              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.content"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={newsEditorConfig}
                        data={field.value || ""}
                        disabled={disabled}
                        onReady={(editor) => {
                          console.log("Editor is ready to use!", editor);
                          editor.setData(
                            defaultValues.localizations?.en?.content || ""
                          );
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        onBlur={(event, editor) => {
                          console.log("Blur.", editor);
                        }}
                        onFocus={(event, editor) => {
                          console.log("Focus.", editor);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Box fontWeight={900} fontSize={13} mt={4} mb={2} width="100%">
                <Typography variant="p">Content (Chinese)</Typography>
              </Box>

              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.content"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={newsEditorConfig}
                        data={field.value || ""}
                        disabled={disabled}
                        onReady={(editor) => {
                          console.log("Editor is ready to use!", editor);
                          editor.setData(
                            defaultValues.localizations?.tc?.content || ""
                          );
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        onBlur={(event, editor) => {
                          console.log("Blur.", editor);
                        }}
                        onFocus={(event, editor) => {
                          console.log("Focus.", editor);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Grid
            container
            item
            xs={6}
            direction="row"
            spacing={3}
            style={{ marginTop: "32px" }}
          >
            <Grid item xs={12}>
              <ImageUploadField
                field="image"
                value={watchedValues.image || ""}
                onUpload={handleImageUpload}
                onDelete={handleImageDelete}
                label="Upload Thumbnail Image"
                classes={classes}
              />
            </Grid>
          </Grid>

          <Grid
            container
            item
            xs={6}
            direction="row"
            spacing={3}
            style={{ marginTop: "32px" }}
          >
            <Grid item xs={12}>
              <ImageUploadField
                field="localizations.en.image"
                value={watchedValues.localizations?.en?.image || ""}
                onUpload={handleImageUpload}
                onDelete={handleImageDelete}
                label="Upload Image (English)"
                classes={classes}
              />
            </Grid>
          </Grid>

          <Grid container item xs={6} direction="row" spacing={3}>
            <Grid item xs={12}>
              <ImageUploadField
                field="localizations.tc.image"
                value={watchedValues.localizations?.tc?.image || ""}
                onUpload={handleImageUpload}
                onDelete={handleImageDelete}
                label="Upload Image (Chinese)"
                classes={classes}
              />
            </Grid>
          </Grid>

          <Box style={{ marginTop: "10px" }}>
            <Grid item xs={12}>
              <strong className={classes.sectionText}>Publish Settings</strong>
            </Grid>
          </Box>
          <Grid container item xs={8}>
            <Controller
              name={`publishTime`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <KeyboardDateTimePicker
                  fullWidth
                  variant="dialog"
                  format="d MMM yyyy, hh:mma"
                  margin="dense"
                  id="date-picker-inline"
                  label="Date Time"
                  value={field.value ? dayjs(field.value) : null}
                  disabled={disabled}
                  onChange={(value) => {
                    setValue(
                      "publishTime",
                      value ? dayjs(value).format("YYYY-MM-DDTHH:mm:00") : null
                    );
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: {
                      fontWeight: 700,
                      color: "#333333",
                    },
                  }}
                  KeyboardButtonProps={{
                    "aria-label": "change date and time",
                  }}
                  helperText="Your post will be posted immediately if you don't set a custom date and time."
                />
              )}
            />
          </Grid>

          <Grid
            container
            item
            xs={12}
            spacing={3}
            style={{ marginTop: "32px", marginLeft: 0 }}
          >
            <Grid item xs={3} style={{ paddingLeft: "4px" }}>
              <Controller
                control={control}
                name="status"
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <ToggleButtonGroup
                    {...rest}
                    value={value || "ACTIVE"}
                    onChange={(event, newFormats) => {
                      onChange(newFormats);
                    }}
                    exclusive
                    aria-label="Status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value="ACTIVE"
                      aria-label="Active"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value="INACTIVE"
                      aria-label="Inactive"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Inactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="type"
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={value === ""}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.checked ? "" : "public")}
                      />
                    }
                    label="Paid member only"
                  />
                )}
              />
            </Grid>
          </Grid>
        </fieldset>
        
        {!disabled && (
          <div className={classes.buttonGroup}>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              style={{
                color: "white",
                background: "#306F9D",
                marginRight: "5px",
                padding: "9px 16px",
              }}
            >
              Publish
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
